import {
  createShopApiGraphqlClient,
  type ShopApiGraphqlClient,
} from "../../../packages/pvc-product/shop-api-transport";
import {
  BILLING_EMAIL_ERROR,
  normalizeBillingEmail,
} from "./customer-validation";

export type BillingCustomerKind = "private" | "business";

export type BillingAddress = {
  id: string;
  fullName: string | null;
  company: string | null;
  streetLine1: string;
  streetLine2: string | null;
  city: string | null;
  province: string | null;
  postalCode: string | null;
  countryCode: string;
  phoneNumber: string | null;
  defaultShippingAddress: boolean;
  defaultBillingAddress: boolean;
};

export type BillingProfile = {
  firstName: string;
  lastName: string;
  emailAddress: string;
  customerType: BillingCustomerKind;
  businessName: string | null;
  businessId: string | null;
  billingName: string | null;
  billingEmail: string | null;
  addresses: BillingAddress[];
};

export class AccountBillingDomainError extends Error {
  constructor(public readonly errorCode: string, message: string) {
    super(message);
    this.name = "AccountBillingDomainError";
  }
}

const BILLING_FIELDS = `
  firstName
  lastName
  emailAddress
  customFields {
    customerType
    businessName
    businessId
    billingName
    billingEmail
  }
  addresses {
    id
    fullName
    company
    streetLine1
    streetLine2
    city
    province
    postalCode
    country { code }
    phoneNumber
    defaultShippingAddress
    defaultBillingAddress
  }
`;

const LOAD_BILLING = `
  query HappyPrintBillingProfile {
    activeCustomer { ${BILLING_FIELDS} }
  }
`;

const UPDATE_BILLING = `
  mutation HappyPrintUpdateBillingProfile($input: UpdateCustomerInput!) {
    updateCustomer(input: $input) { ${BILLING_FIELDS} }
  }
`;

const UPDATE_ADDRESS = `
  mutation HappyPrintSetDefaultBillingAddress($input: UpdateAddressInput!) {
    updateCustomerAddress(input: $input) { id }
  }
`;

function object(value: unknown, path: string): Record<string, unknown> {
  if (value == null || typeof value !== "object" || Array.isArray(value)) throw new Error(`${path} must be an object`);
  return value as Record<string, unknown>;
}

function string(value: unknown, path: string): string {
  if (typeof value !== "string") throw new Error(`${path} must be a string`);
  return value;
}

function nullableString(value: unknown, path: string): string | null {
  if (value == null) return null;
  if (typeof value !== "string") throw new Error(`${path} must be a string or null`);
  return value || null;
}

function boolean(value: unknown, path: string): boolean {
  if (typeof value !== "boolean") throw new Error(`${path} must be a boolean`);
  return value;
}

function decodeAddress(value: unknown, path: string): BillingAddress {
  const address = object(value, path);
  const country = object(address.country, `${path}.country`);
  return {
    id: string(address.id, `${path}.id`),
    fullName: nullableString(address.fullName, `${path}.fullName`),
    company: nullableString(address.company, `${path}.company`),
    streetLine1: string(address.streetLine1, `${path}.streetLine1`),
    streetLine2: nullableString(address.streetLine2, `${path}.streetLine2`),
    city: nullableString(address.city, `${path}.city`),
    province: nullableString(address.province, `${path}.province`),
    postalCode: nullableString(address.postalCode, `${path}.postalCode`),
    countryCode: string(country.code, `${path}.country.code`),
    phoneNumber: nullableString(address.phoneNumber, `${path}.phoneNumber`),
    defaultShippingAddress: boolean(address.defaultShippingAddress, `${path}.defaultShippingAddress`),
    defaultBillingAddress: boolean(address.defaultBillingAddress, `${path}.defaultBillingAddress`),
  };
}

function decodeProfile(value: unknown): BillingProfile | null {
  if (value == null) return null;
  const customer = object(value, "data.activeCustomer");
  const customFields = customer.customFields == null ? {} : object(customer.customFields, "data.activeCustomer.customFields");
  if (!Array.isArray(customer.addresses)) throw new Error("data.activeCustomer.addresses must be an array");
  return {
    firstName: string(customer.firstName, "data.activeCustomer.firstName"),
    lastName: string(customer.lastName, "data.activeCustomer.lastName"),
    emailAddress: string(customer.emailAddress, "data.activeCustomer.emailAddress"),
    customerType: customFields.customerType === "business" ? "business" : "private",
    businessName: nullableString(customFields.businessName, "data.activeCustomer.customFields.businessName"),
    businessId: nullableString(customFields.businessId, "data.activeCustomer.customFields.businessId"),
    billingName: nullableString(customFields.billingName, "data.activeCustomer.customFields.billingName"),
    billingEmail: nullableString(customFields.billingEmail, "data.activeCustomer.customFields.billingEmail"),
    addresses: customer.addresses.map((item, index) => decodeAddress(item, `data.activeCustomer.addresses[${index}]`)),
  };
}

export function createAccountBillingApi(client: ShopApiGraphqlClient) {
  const load = async (signal?: AbortSignal): Promise<BillingProfile | null> => {
    const raw = await client.execute({ operationName: "HappyPrintBillingProfile", document: LOAD_BILLING, signal });
    const data = object(raw, "data");
    return decodeProfile(data.activeCustomer);
  };

  return {
    load,

    async save(_profile: BillingProfile, billingName: string, billingEmail: string): Promise<BillingProfile> {
      const normalizedName = billingName.trim();
      if (!normalizedName) {
        throw new AccountBillingDomainError("BILLING_NAME_REQUIRED", "יש להזין שם לחיוב.");
      }

      const normalizedEmail = normalizeBillingEmail(billingEmail);
      if (!normalizedEmail) {
        throw new AccountBillingDomainError("INVALID_BILLING_EMAIL", BILLING_EMAIL_ERROR);
      }

      const raw = await client.execute({
        operationName: "HappyPrintUpdateBillingProfile",
        document: UPDATE_BILLING,
        variables: {
          input: {
            customFields: {
              billingName: normalizedName,
              billingEmail: normalizedEmail,
            },
          },
        },
      });
      const data = object(raw, "data");
      const next = decodeProfile(data.updateCustomer);
      if (!next) throw new Error("updateCustomer returned no customer");
      return next;
    },

    async setDefaultBillingAddress(address: BillingAddress): Promise<BillingProfile | null> {
      await client.execute({
        operationName: "HappyPrintSetDefaultBillingAddress",
        document: UPDATE_ADDRESS,
        variables: {
          input: {
            id: address.id,
            defaultBillingAddress: true,
          },
        },
      });
      return load();
    },
  };
}

export function createBrowserAccountBillingApi() {
  const endpoint = import.meta.env.VITE_SHOP_API_URL || "/shop-api";
  return createAccountBillingApi(createShopApiGraphqlClient({ endpoint }));
}
